"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type AccountType = "personal" | "business" | null;

export interface RegistrationState {
  accountType: AccountType;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  mobileCountryCode: string;
  password: string;
  emailVerified: boolean;
  mobileVerified: boolean;
  privacyAccepted: boolean;
  // Company details (for business accounts)
  companyName: string;
  companyNumber: string;
  companyCountry: string;
  companyRelationship: string;
}

interface RegistrationContextValue {
  state: RegistrationState;
  setAccountType: (type: AccountType) => void;
  setFirstName: (name: string) => void;
  setLastName: (name: string) => void;
  setEmail: (email: string) => void;
  setMobile: (mobile: string) => void;
  setMobileCountryCode: (code: string) => void;
  setPassword: (password: string) => void;
  setPrivacyAccepted: (accepted: boolean) => void;
  setCompanyName: (name: string) => void;
  setCompanyNumber: (number: string) => void;
  setCompanyCountry: (country: string) => void;
  setCompanyRelationship: (relationship: string) => void;
  sendEmailOtp: () => Promise<boolean>;
  verifyEmailOtp: (code: string) => Promise<boolean>;
  sendMobileOtp: () => Promise<boolean>;
  verifyMobileOtp: (code: string) => Promise<boolean>;
  submitRegistration: () => Promise<boolean>;
  resetState: () => void;
}

const initialState: RegistrationState = {
  accountType: null,
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  mobileCountryCode: "+44",
  password: "",
  emailVerified: false,
  mobileVerified: false,
  privacyAccepted: false,
  // Company details
  companyName: "",
  companyNumber: "",
  companyCountry: "",
  companyRelationship: "",
};

const RegistrationContext = createContext<RegistrationContextValue | null>(
  null
);

export function useRegistration() {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error(
      "useRegistration must be used within a RegistrationProvider"
    );
  }
  return context;
}

interface RegistrationProviderProps {
  children: React.ReactNode;
}

export function RegistrationProvider({ children }: RegistrationProviderProps) {
  const [state, setState] = useState<RegistrationState>(initialState);

  const setAccountType = useCallback((type: AccountType) => {
    setState((prev) => ({ ...prev, accountType: type }));
  }, []);

  const setFirstName = useCallback((firstName: string) => {
    setState((prev) => ({ ...prev, firstName }));
  }, []);

  const setLastName = useCallback((lastName: string) => {
    setState((prev) => ({ ...prev, lastName }));
  }, []);

  const setEmail = useCallback((email: string) => {
    setState((prev) => ({ ...prev, email }));
  }, []);

  const setMobile = useCallback((mobile: string) => {
    setState((prev) => ({ ...prev, mobile }));
  }, []);

  const setMobileCountryCode = useCallback((mobileCountryCode: string) => {
    setState((prev) => ({ ...prev, mobileCountryCode }));
  }, []);

  const setPrivacyAccepted = useCallback((privacyAccepted: boolean) => {
    setState((prev) => ({ ...prev, privacyAccepted }));
  }, []);

  const setPassword = useCallback((password: string) => {
    setState((prev) => ({ ...prev, password }));
  }, []);

  const setCompanyName = useCallback((companyName: string) => {
    setState((prev) => ({ ...prev, companyName }));
  }, []);

  const setCompanyNumber = useCallback((companyNumber: string) => {
    setState((prev) => ({ ...prev, companyNumber }));
  }, []);

  const setCompanyCountry = useCallback((companyCountry: string) => {
    setState((prev) => ({ ...prev, companyCountry }));
  }, []);

  const setCompanyRelationship = useCallback((companyRelationship: string) => {
    setState((prev) => ({ ...prev, companyRelationship }));
  }, []);

  // Dummy API: Send email OTP
  const sendEmailOtp = useCallback(async (): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    // TODO: Replace with actual API call
    console.warn("Sending email OTP to:", state.email);
    return true;
  }, [state.email]);

  // Dummy API: Verify email OTP
  const verifyEmailOtp = useCallback(async (code: string): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    // TODO: Replace with actual API call
    console.warn("Verifying email OTP:", code);
    // Accept any 6-digit code for now
    if (code.length === 6) {
      setState((prev) => ({ ...prev, emailVerified: true }));
      return true;
    }
    return false;
  }, []);

  // Dummy API: Send mobile OTP
  const sendMobileOtp = useCallback(async (): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    // TODO: Replace with actual API call
    console.warn(
      "Sending mobile OTP to:",
      state.mobileCountryCode,
      state.mobile
    );
    return true;
  }, [state.mobileCountryCode, state.mobile]);

  // Dummy API: Verify mobile OTP
  const verifyMobileOtp = useCallback(
    async (code: string): Promise<boolean> => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // TODO: Replace with actual API call
      console.warn("Verifying mobile OTP:", code);
      // Accept any 6-digit code for now
      if (code.length === 6) {
        setState((prev) => ({ ...prev, mobileVerified: true }));
        return true;
      }
      return false;
    },
    []
  );

  // Dummy API: Submit registration
  const submitRegistration = useCallback(async (): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    // TODO: Replace with actual API call
    console.warn("Submitting registration:", state);
    return true;
  }, [state]);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  // Memoized context value (render-stability.md Rule 3). The setters are all
  // useCallback-stable, so this only changes when `state` does.
  const value = useMemo<RegistrationContextValue>(
    () => ({
      state,
      setAccountType,
      setFirstName,
      setLastName,
      setEmail,
      setMobile,
      setMobileCountryCode,
      setPassword,
      setPrivacyAccepted,
      setCompanyName,
      setCompanyNumber,
      setCompanyCountry,
      setCompanyRelationship,
      sendEmailOtp,
      verifyEmailOtp,
      sendMobileOtp,
      verifyMobileOtp,
      submitRegistration,
      resetState,
    }),
    [
      state,
      setAccountType,
      setFirstName,
      setLastName,
      setEmail,
      setMobile,
      setMobileCountryCode,
      setPassword,
      setPrivacyAccepted,
      setCompanyName,
      setCompanyNumber,
      setCompanyCountry,
      setCompanyRelationship,
      sendEmailOtp,
      verifyEmailOtp,
      sendMobileOtp,
      verifyMobileOtp,
      submitRegistration,
      resetState,
    ]
  );

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
}

export default RegistrationProvider;
